import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Shield } from "lucide-react";
import { toast } from "sonner";
import JeaButton from "@/components/jea/JeaButton";
import {
  JUDGMENT_FIRST_SUBSTEPS,
  JUDGMENT_FIRST_VALIDATORS,
  JUDGMENT_FIRST_HARD_BLOCK,
  isJudgmentFirstReady,
  JudgmentFirstForm,
  JudgmentFirstPills,
} from "@/components/debtors/JudgmentFirstForm";
import { JudgmentInsightPanel } from "@/components/debtors/JudgmentInsightPanel";
import { apiClient, formatApiError } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { TIERS, formatMoney } from "@/lib/pricing";
import { cn } from "@/lib/utils";

const emptyDebtor = { assets: {}, judgment: {}, current_address: {}, current_employer: { address: {} } };

const StandardAdd = ({ onClose }) => {
  const nav = useNavigate();
  const [subIndex, setSubIndex] = useState(0);
  const [debtor, setDebtor] = useState(emptyDebtor);
  const [tier, setTier] = useState("bronze");
  const [submitting, setSubmitting] = useState(false);

  const patch = (partial) => setDebtor((d) => ({ ...d, ...partial }));
  const currentSubstep = JUDGMENT_FIRST_SUBSTEPS[subIndex];
  const substepValid = currentSubstep
    ? (JUDGMENT_FIRST_VALIDATORS[currentSubstep.key] || (() => true))(debtor)
    : true;
  const hardBlocked = JUDGMENT_FIRST_HARD_BLOCK(debtor);
  const last = subIndex === JUDGMENT_FIRST_SUBSTEPS.length - 1;
  const ready = isJudgmentFirstReady(debtor);

  const submit = async () => {
    if (!ready) {
      toast.error("Fill Judgment Essentials, Court + Case Info, and Lien & Renewal Status to continue.");
      return;
    }
    setSubmitting(true);
    try {
      await apiClient.post("/debtors", { ...debtor, tier });
      toast.success("Judgment added.");
      onClose();
      nav("/debtors");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="w-full max-w-[720px] max-h-[90vh] overflow-y-auto bg-bg-secondary border border-border-default rounded-[16px] p-10 shadow-elevated" data-testid="add-debtor-modal">
      <div className="flex items-start justify-between mb-2">
        <div className="space-y-1">
          <h2 className="text-white font-bold text-[22px]">Add a New Judgment</h2>
          <p className="text-text-secondary text-[13px]">
            Judgment-first. Only 3 substeps are required — everything else is optional.
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-2 text-text-secondary hover:text-white"
          data-testid="add-debtor-close"
        >
          <X size={16} />
        </button>
      </div>

      <JudgmentFirstPills index={subIndex} onSelect={setSubIndex} />
      <JudgmentFirstForm
        debtor={debtor}
        patch={patch}
        subIndex={subIndex}
        onSkipOptional={() => setSubIndex(JUDGMENT_FIRST_SUBSTEPS.length - 1)}
      />

      {last && (
        <div className="mt-6">
          <div className="jea-caption mb-2">Tier</div>
          <div className="flex gap-2">
            {TIERS.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTier(t.key)}
                className={cn(
                  "flex-1 py-3 rounded-[8px] border flex flex-col items-center gap-1 transition-all",
                  tier === t.key
                    ? "bg-bg-primary border-accent-pink shadow-pink-glow"
                    : "bg-bg-primary border-border-default hover:border-accent-pink/60"
                )}
                data-testid={`add-debtor-tier-${t.key}`}
              >
                <Shield size={20} style={{ color: t.color }} />
                <span className="text-[11px] font-bold tracking-[0.1em]" style={{ color: t.color }}>{t.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="mt-8 flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => (subIndex === 0 ? onClose() : setSubIndex(subIndex - 1))}
          className="h-11 px-5 rounded-[8px] border border-border-default text-text-secondary hover:text-white hover:bg-bg-tertiary transition-colors text-[14px] font-semibold"
          data-testid="add-debtor-back"
        >
          {subIndex === 0 ? "Cancel" : "Back"}
        </button>
        {last ? (
          <JeaButton
            onClick={submit}
            loading={submitting}
            disabled={!ready || hardBlocked}
            data-testid="add-debtor-submit"
          >
            Add Judgment
          </JeaButton>
        ) : (
          <JeaButton
            onClick={() => setSubIndex(subIndex + 1)}
            disabled={!substepValid || hardBlocked}
            data-testid="add-debtor-continue"
          >
            Continue
          </JeaButton>
        )}
      </div>
    </div>
  );
};

const ProAdd = ({ onClose }) => {
  const nav = useNavigate();
  const [debtor, setDebtor] = useState(emptyDebtor);
  const [tier, setTier] = useState("bronze");
  const [submitting, setSubmitting] = useState(false);

  const patch = (partial) => setDebtor((d) => ({ ...d, ...partial }));
  const j = debtor.judgment || {};
  const ready = isJudgmentFirstReady(debtor);
  const hardBlocked = JUDGMENT_FIRST_HARD_BLOCK(debtor);
  const insightReady = !!(j.state && j.date_entered);

  const submit = async () => {
    if (!ready) {
      toast.error("Fill Judgment Essentials, Court + Case Info, and Lien & Renewal Status to continue.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await apiClient.post("/debtors", { ...debtor, tier });
      toast.success("Judgment added.");
      onClose();
      // Land on the profile so the Insight Panel is visible immediately (per Phase 9B spec).
      if (data?.id) nav(`/debtors/${data.id}`);
      else nav("/debtors");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="w-full max-w-[1240px] max-h-[92vh] overflow-y-auto bg-bg-secondary border border-border-default rounded-[16px] shadow-elevated" data-testid="add-debtor-modal">
      <div className="grid grid-cols-[minmax(0,1fr)_340px] gap-0">
        <div className="p-10 min-w-0">
          <div className="flex items-start justify-between mb-4">
            <div className="space-y-1">
              <h2 className="text-white font-bold text-[22px]">Add a New Judgment</h2>
              <p className="text-text-secondary text-[13px]">
                PRO advanced mode — all substeps on one page. Judgment-first: 3 substeps are required, 2 are optional.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-2 text-text-secondary hover:text-white"
              data-testid="add-debtor-close"
            >
              <X size={16} />
            </button>
          </div>
          <JudgmentFirstForm
            debtor={debtor}
            patch={patch}
            subIndex={null}
            onSkipOptional={() => {}}
          />

          {insightReady && (
            <div className="mt-10 pt-6 border-t border-border-default" data-testid="pro-inline-insight">
              <JudgmentInsightPanel
                payload={{
                  state: j.state,
                  date_entered: j.date_entered,
                  court_level_variant: j.court_level_variant,
                  lien_filed: j.lien_filed,
                  lien_filed_at: j.lien_filed_at,
                  known_real_property_states: j.known_real_property_states,
                }}
                debtorName={debtor.full_name}
                judgmentAmountLabel={j.amount_cents ? formatMoney(j.amount_cents) : undefined}
                layout="stack"
              />
            </div>
          )}
        </div>

        <aside className="border-l border-border-default bg-bg-primary/60 p-8 space-y-4 sticky top-0 self-start" data-testid="add-debtor-summary">
          <div className="jea-caption">Summary</div>
          <div>
            <div className="text-[13px] text-text-secondary">Name</div>
            <div className="text-white font-semibold text-[15px]" data-testid="summary-name">
              {debtor.full_name || "—"}
            </div>
          </div>
          <div>
            <div className="text-[13px] text-text-secondary">Judgment</div>
            <div className="jea-mono text-accent-pink font-bold text-[16px]" data-testid="summary-amount">
              {formatMoney(j.amount_cents || 0)}
            </div>
          </div>
          {j.state && (
            <div>
              <div className="text-[13px] text-text-secondary">Jurisdiction</div>
              <div className="text-white font-semibold text-[13px]" data-testid="summary-state">
                {j.state}
                {j.court_level_variant ? ` · ${j.court_level_variant.replace(/_/g, " ")}` : ""}
              </div>
            </div>
          )}
          <div>
            <div className="jea-caption mb-2">Tier</div>
            <div className="flex gap-1">
              {TIERS.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setTier(t.key)}
                  className={cn(
                    "flex-1 py-2 rounded-[8px] border flex flex-col items-center gap-1 transition-all",
                    tier === t.key
                      ? "bg-bg-secondary border-accent-pink shadow-pink-glow"
                      : "bg-bg-primary border-border-default hover:border-accent-pink/60"
                  )}
                  data-testid={`add-debtor-tier-${t.key}`}
                >
                  <Shield size={16} style={{ color: t.color }} />
                  <span className="text-[10px] font-bold tracking-[0.1em]" style={{ color: t.color }}>{t.label}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="pt-2 space-y-2">
            <JeaButton
              fullWidth
              onClick={submit}
              loading={submitting}
              disabled={!ready || hardBlocked}
              data-testid="add-debtor-submit"
            >
              Add Judgment
            </JeaButton>
            <button
              type="button"
              onClick={onClose}
              className="w-full h-11 rounded-[8px] border border-border-default text-text-secondary hover:text-white hover:bg-bg-tertiary transition-colors text-[13px] font-semibold"
              data-testid="add-debtor-cancel"
            >
              Cancel
            </button>
          </div>
        </aside>
      </div>
    </div>
  );
};

const AddDebtorPage = () => {
  const { user } = useAuth();
  const nav = useNavigate();
  const close = () => nav("/debtors");

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[90] bg-black/60 backdrop-blur-[12px] flex items-center justify-center px-4 py-6"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        onClick={close}
        data-testid="add-debtor-overlay"
      >
        <div onClick={(e) => e.stopPropagation()}>
          {user?.account_type === "pro" ? <ProAdd onClose={close} /> : <StandardAdd onClose={close} />}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AddDebtorPage;
